import { useState, useEffect, useCallback } from 'react';
import { CreationResponse, CreationStatus } from '../../api/client';
import { useTranslation } from '../../i18n';

interface BestMomentsProcessingStateProps {
  creation: CreationResponse;
  onStatusChange?: (updatedCreation: CreationResponse) => void;
}

// Processing time for best moments in milliseconds (10 minutes)
const PROCESSING_TIME = 10 * 60 * 1000;

export function BestMomentsProcessingState({ creation, onStatusChange }: BestMomentsProcessingStateProps) {
  const { t } = useTranslation();
  const [progress, setProgress] = useState(0);
  const [messageIndex, setMessageIndex] = useState(0);
  const [currentMessage, setCurrentMessage] = useState<string>('');
  const [isExtendedProcessing, setIsExtendedProcessing] = useState(false);
  const [extendedMessageIndex, setExtendedMessageIndex] = useState(0);
  const [isEarlyCompletion, setIsEarlyCompletion] = useState(false);
  const [earlyCompletionMessage, setEarlyCompletionMessage] = useState<string>('');
  
  // Get translated message arrays
  const getProcessingMessages = useCallback(() => {
    const count = 24; // Based on the number of messages in the translation file
    return Array.from({ length: count }, (_, i) =>
      t(`bestMomentsProcessing.processingMessages.${i}`)
    );
  }, [t]);
  
  const getExtendedProcessingMessages = useCallback(() => {
    const count = 10; // Based on the number of extended messages in the translation file
    return Array.from({ length: count }, (_, i) =>
      t(`bestMomentsProcessing.extendedProcessingMessages.${i}`)
    );
  }, [t]);
  
  const getEarlyCompletionMessages = useCallback(() => {
    const count = 5; // Based on the number of early completion messages in the translation file
    return Array.from({ length: count }, (_, i) =>
      t(`bestMomentsProcessing.earlyCompletionMessages.${i}`)
    );
  }, [t]);
  
  // Initialize messages on mount
  useEffect(() => {
    const processingMessages = getProcessingMessages();
    const initialMessageIndex = Math.floor(Math.random() * processingMessages.length);
    setMessageIndex(initialMessageIndex);
    setCurrentMessage(processingMessages[initialMessageIndex]);
  }, [getProcessingMessages]);
  
  // Check for status changes that would trigger early completion
  useEffect(() => {
    // If status is COMPLETED and we're not at 100% yet, show early completion
    if ((creation.status === CreationStatus.COMPLETED || creation.status === CreationStatus.FAILED) && 
        progress < 99 && 
        !isEarlyCompletion && 
        creation.status === CreationStatus.COMPLETED) {
      
      setIsEarlyCompletion(true);
      // Pick a random early completion message
      const earlyCompletionMessages = getEarlyCompletionMessages();
      const randomIndex = Math.floor(Math.random() * earlyCompletionMessages.length);
      setEarlyCompletionMessage(earlyCompletionMessages[randomIndex]);
      
      // Animate to 100% quickly
      let currentProgress = progress;
      const progressInterval = setInterval(() => {
        currentProgress += 5;
        if (currentProgress >= 100) {
          clearInterval(progressInterval);
          currentProgress = 100;
          
          // After a short delay, notify the parent to update the view
          setTimeout(() => {
            if (onStatusChange) {
              onStatusChange(creation);
            }
          }, 1500); // Show the 100% state for 1.5 seconds
        }
        setProgress(currentProgress);
      }, 50); // Update every 50ms for a smooth animation
    }
  }, [creation.status, progress, isEarlyCompletion, onStatusChange, creation, getEarlyCompletionMessages]);
  
  useEffect(() => {
    // Don't update progress if we're in early completion mode
    if (isEarlyCompletion) return;
    
    // Calculate progress based on updated_at time
    const calculateProgress = () => {
      const updatedAt = new Date(creation.updated_at);
      const estimatedCompletionTime = new Date(updatedAt.getTime() + PROCESSING_TIME);
      const now = new Date();
      
      // Calculate elapsed time since update
      const elapsedTime = now.getTime() - updatedAt.getTime();
      
      // Check if we've exceeded the estimated processing time
      if (elapsedTime > PROCESSING_TIME) {
        setIsExtendedProcessing(true);
        return {
          progress: 99,
          isExtended: true,
          remainingMinutes: 0,
          remainingSeconds: 0
        };
      }
      
      // Calculate progress percentage (capped at 99% until complete)
      const calculatedProgress = Math.min(99, Math.floor((elapsedTime / PROCESSING_TIME) * 100));
      
      setProgress(calculatedProgress);
      
      // Calculate estimated time remaining in minutes and seconds
      const remainingTime = Math.max(0, estimatedCompletionTime.getTime() - now.getTime());
      const remainingMinutes = Math.floor(remainingTime / 60000);
      const remainingSeconds = Math.floor((remainingTime % 60000) / 1000);
      
      return {
        progress: calculatedProgress,
        isExtended: false,
        remainingMinutes,
        remainingSeconds
      };
    };
    
    // Initial calculation
    calculateProgress();
    
    // Update progress every second
    const progressInterval = setInterval(() => {
      const { progress, isExtended } = calculateProgress();
      setProgress(progress);
      
      // If we've entered extended processing, we might want to change the message set
      if (isExtended && !isExtendedProcessing) {
        setIsExtendedProcessing(true);
        const extendedMessages = getExtendedProcessingMessages();
        setExtendedMessageIndex(0);
        setCurrentMessage(extendedMessages[0]);
      }
    }, 1000);
    
    // Change message every 15 seconds
    const messageInterval = setInterval(() => {
      if (isExtendedProcessing) {
        // Use extended processing messages
        const extendedMessages = getExtendedProcessingMessages();
        let newIndex;
        do {
          newIndex = Math.floor(Math.random() * extendedMessages.length);
        } while (newIndex === extendedMessageIndex && extendedMessages.length > 1);
        
        setExtendedMessageIndex(newIndex);
        setCurrentMessage(extendedMessages[newIndex]);
      } else {
        // Use regular processing messages
        const processingMessages = getProcessingMessages();
        let newIndex;
        do {
          newIndex = Math.floor(Math.random() * processingMessages.length);
        } while (newIndex === messageIndex && processingMessages.length > 1);
        
        setMessageIndex(newIndex);
        setCurrentMessage(processingMessages[newIndex]);
      }
    }, 15000);
    
    return () => {
      clearInterval(progressInterval);
      clearInterval(messageInterval);
    };
  }, [
    creation.updated_at,
    messageIndex,
    extendedMessageIndex,
    isExtendedProcessing,
    isEarlyCompletion,
    getExtendedProcessingMessages,
    getProcessingMessages,
  ]);
  
  // Format the estimated completion time
  const formatEstimatedTime = () => {
    if (isEarlyCompletion) {
      return t('bestMomentsProcessing.finishingUp');
    }
    
    if (isExtendedProcessing) {
      return t('bestMomentsProcessing.almostThere');
    }
    
    const updatedAt = new Date(creation.updated_at);
    const estimatedCompletionTime = new Date(updatedAt.getTime() + PROCESSING_TIME);
    const now = new Date();
    
    // Calculate remaining time in minutes and seconds
    const remainingTime = Math.max(0, estimatedCompletionTime.getTime() - now.getTime());
    const remainingMinutes = Math.floor(remainingTime / 60000);
    const remainingSeconds = Math.floor((remainingTime % 60000) / 1000);
    
    if (remainingMinutes <= 0 && remainingSeconds <= 0) {
      return t('bestMomentsProcessing.almostDone');
    }
    
    return t('bestMomentsProcessing.estimatedTime', { minutes: remainingMinutes, seconds: remainingSeconds });
  };
  
  return (
    <div className="bg-surface-interactive dark:bg-surface-interactive-dark border border-interactive dark:border-interactive-dark rounded-2xl p-6 text-center shadow-interactive">
      <div className="flex flex-col items-center">
        {/* Podcast Best Moments title */}
        <h2 className="text-xl font-bold text-content-strong mb-4">
          {t('create.types.podcast-moments.header')}
        </h2>
      
        {/* Circular progress indicator - only show when not in extended processing */}
        {!isExtendedProcessing || isEarlyCompletion ? (
          <div className="relative w-32 h-32 mb-4">
            <svg className="w-full h-full" viewBox="0 0 100 100">
              {/* Background circle */}
              <circle
                className="text-progress-track dark:text-progress-track-dark stroke-current"
                strokeWidth="8"
                cx="50"
                cy="50"
                r="40"
                fill="transparent"
              />
              {/* Progress circle */}
              <circle
                className="text-primary-accent stroke-current"
                strokeWidth="8"
                strokeLinecap="round"
                cx="50"
                cy="50"
                r="40"
                fill="transparent"
                strokeDasharray="251.2"
                strokeDashoffset={251.2 - (251.2 * progress) / 100}
                transform="rotate(-90 50 50)"
              />
            </svg>
            
            {/* Spinner animation around the circle */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-40 h-40 border-4 border-transparent border-t-primary-accent-alpha-30 rounded-full animate-spin"></div>
            </div>
            
            {/* Percentage text - positioned absolutely for better theme support */}
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-2xl font-bold text-content-strong">
                {progress}%
              </span>
            </div>
          </div>
        ) : (
          /* Extended processing spinner */
          <div className="relative w-32 h-32 mb-4 flex items-center justify-center">
            <div className="w-20 h-20 border-4 border-primary-accent-alpha-30 border-t-primary-accent rounded-full animate-spin"></div>
          </div>
        )}
        
        <h3 className="text-xl font-medium text-content-strong mb-2">
          {isEarlyCompletion 
            ? earlyCompletionMessage
            : isExtendedProcessing 
              ? t('bestMomentsProcessing.stillProcessingHeader')
              : t('bestMomentsProcessing.extractingHeader')}
        </h3>
        
        <p className="text-theme-hint mb-4">
          {formatEstimatedTime()}
        </p>
        
        {/* Processing message with loading animation - smaller dots */}
        <div className="flex items-center gap-2 text-content-emphasis bg-primary-accent-alpha-10 px-4 py-2 rounded-full">
          <div className="flex space-x-1">
            <div className="w-1.5 h-1.5 bg-primary-accent rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-1.5 h-1.5 bg-primary-accent rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-1.5 h-1.5 bg-primary-accent rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
          <span>{isEarlyCompletion ? t('bestMomentsProcessing.finalizingEarlyCompletion') : currentMessage}</span>
        </div>
        
        {/* Description of what's happening */}
        <div className="mt-6 text-sm text-theme-hint max-w-sm">
          <p>{t('bestMomentsProcessing.description')}</p>
        </div>
      </div>
    </div>
  );
}
